import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Sun, Zap, Battery, Wind, Flame, Snowflake,
  ArrowRight, CheckCircle2,
} from "lucide-react";

const SOLAR_IMG =
  "https://static.prod-images.emergentagent.com/jobs/f79a6853-bb27-42e4-97ca-1ec91afb31f3/images/58f89974e93b472b880c66ce9088dddaf55b5b7c98ffd61536af91eb39709f1f.png";
const BESS_IMG =
  "https://static.prod-images.emergentagent.com/jobs/f79a6853-bb27-42e4-97ca-1ec91afb31f3/images/172811d61696c9206cf7de9a53cce073e2d4d6a6aace276868b920f0ec103e37.png";
const OPEN_ACCESS_IMG =
  "https://images.pexels.com/photos/9799994/pexels-photo-9799994.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

function SectionReveal({ children, className = "" }) {
  return (
    <motion.section
      initial={{ opacity: 0, y: 40 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-100px" }}
      transition={{ duration: 0.7 }}
      className={className}
    >
      {children}
    </motion.section>
  );
}

function CheckList({ items, light = true }) {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-8">
      {items.map((b, i) => (
        <div
          key={i}
          className={`flex items-center gap-2 text-sm ${
            light ? "text-[#0A1628]/80" : "text-white/80"
          }`}
        >
          <CheckCircle2 size={16} className="text-[#00D68F] shrink-0" /> {b}
        </div>
      ))}
    </div>
  );
}

export default function ServicesPage() {
  return (
    <div>
      {/* ── Hero ─────────────────────────────────────────── */}
      <section className="relative pt-32 pb-24 bg-[#0A1628]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 relative z-10">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <p className="text-xs font-bold tracking-[0.2em] uppercase text-[#00D68F] mb-4">
              Our Services
            </p>
            <h1 className="text-5xl md:text-6xl font-black tracking-tighter text-white max-w-3xl">
              Powering India's <span className="text-[#00D68F]">Industries</span>
            </h1>
            <p className="text-white/60 text-base md:text-lg mt-6 max-w-2xl">
              From rooftop solar to utility-scale storage, we deliver the full
              spectrum of clean energy solutions.
            </p>
          </motion.div>
        </div>
      </section>

      {/* ── Onsite Solar ─────────────────────────────────── */}
      <SectionReveal className="py-24 md:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid md:grid-cols-12 gap-12 items-center">
          <div className="md:col-span-7">
            <div className="flex items-center gap-3 mb-4">
              <Sun size={28} className="text-[#00D68F]" />
              <p className="text-xs font-bold tracking-[0.2em] uppercase text-[#00D68F]">
                Primary Service
              </p>
            </div>
            <h2 className="text-4xl md:text-5xl font-bold tracking-tight text-[#0A1628] mb-6">
              Onsite Solar
            </h2>
            <p className="text-[#0A1628]/70 text-base md:text-lg leading-relaxed mb-8">
              Rooftop and ground-mount solar installations, fully funded and
              operated by EnergyX. Choose our RESCO/PPA model for zero upfront
              cost, or CapEx model for full ownership.
            </p>
            <CheckList
              items={[
                "Zero CapEx RESCO Model",
                "25-Year PPAs",
                "Savings from Day 1",
                "Professional O&M",
                "Real-time Monitoring",
                "ESG Compliance",
              ]}
            />
            <p className="text-[#0A1628]/50 text-sm">
              <strong>Sectors:</strong> Manufacturing, Automotive, F&B, Textile,
              Hospitality, Packaging
            </p>
          </div>
          <div className="md:col-span-5">
            <img
              src={SOLAR_IMG}
              alt="Onsite Solar"
              className="w-full h-80 object-cover"
              loading="lazy"
            />
          </div>
        </div>
      </SectionReveal>

      {/* ── Open Access ──────────────────────────────────── */}
      <SectionReveal className="py-24 md:py-32 bg-[#F5F7FA]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid md:grid-cols-12 gap-12 items-center">
          <div className="md:col-span-5 order-2 md:order-1">
            <img
              src={OPEN_ACCESS_IMG}
              alt="Open Access Solar"
              className="w-full h-80 object-cover"
              loading="lazy"
            />
          </div>
          <div className="md:col-span-7 order-1 md:order-2">
            <div className="flex items-center gap-3 mb-4">
              <Zap size={28} className="text-[#00D68F]" />
              <p className="text-xs font-bold tracking-[0.2em] uppercase text-[#00D68F]">
                Utility Scale
              </p>
            </div>
            <h2 className="text-4xl md:text-5xl font-bold tracking-tight text-[#0A1628] mb-6">
              Open Access Solar
            </h2>
            <p className="text-[#0A1628]/70 text-base md:text-lg leading-relaxed mb-8">
              MW-scale solar parks across Haryana, Rajasthan, UP, Maharashtra,
              and Gujarat. Power generated at our solar parks is transmitted via
              the state grid directly to your facility.
            </p>
            <CheckList
              items={[
                "MW-Scale Capacity",
                "State-Level Incentives",
                "Banking Provisions",
                "Grid-Connected",
                "Beyond Rooftop Limits",
                "Multi-State Coverage",
              ]}
            />
            <p className="text-[#0A1628]/50 text-sm">
              <strong>Ideal for:</strong> Large C&I consumers with high energy
              demand who want to go beyond rooftop capacity.
            </p>
          </div>
        </div>
      </SectionReveal>

      {/* ── BESS ─────────────────────────────────────────── */}
      <SectionReveal className="py-24 md:py-32 bg-[#0A1628]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid md:grid-cols-12 gap-12 items-center">
          <div className="md:col-span-7">
            <div className="flex items-center gap-3 mb-4">
              <Battery size={28} className="text-[#00D68F]" />
              <p className="text-xs font-bold tracking-[0.2em] uppercase text-[#00D68F]">
                Future Ready
              </p>
            </div>
            <h2 className="text-4xl md:text-5xl font-bold tracking-tight text-white mb-6">
              Battery Energy Storage
            </h2>
            <p className="text-white/70 text-base md:text-lg leading-relaxed mb-8">
              The future of energy is stored. EnergyX is building large-scale
              BESS for peak shaving, grid balancing, demand charge reduction,
              and backup during outages.
            </p>
            <CheckList
              items={[
                "Peak Shaving",
                "Grid Balancing",
                "Demand Charge Reduction",
                "Outage Backup",
              ]}
              light={false}
            />
          </div>
          <div className="md:col-span-5">
            <img
              src={BESS_IMG}
              alt="BESS"
              className="w-full h-80 object-cover"
              loading="lazy"
            />
          </div>
        </div>
      </SectionReveal>

      {/* ── Secondary Services ───────────────────────────── */}
      <SectionReveal className="py-24 md:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <p className="text-xs font-bold tracking-[0.2em] uppercase text-[#00D68F] mb-4">
            Also Available
          </p>
          <h2 className="text-4xl md:text-5xl font-bold tracking-tight text-[#0A1628] mb-16">
            More Solutions
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              {
                icon: Wind,
                title: "Wind Energy",
                desc: "Offsite wind power solutions for large C&I consumers looking to diversify their renewable energy mix.",
              },
              {
                icon: Flame,
                title: "Industrial Heat",
                desc: "Biomass-based thermal solutions for process heating in manufacturing and food processing.",
              },
              {
                icon: Snowflake,
                title: "Cooling Solutions",
                desc: "Greenfield and retrofit cooling systems for industrial facilities, warehouses, and commercial spaces.",
              },
            ].map((s, i) => {
              const Icon = s.icon;
              return (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.15 }}
                  className="bg-white border border-gray-200 p-8 hover:shadow-xl hover:-translate-y-1 transition-all"
                >
                  <Icon size={32} className="text-[#00D68F] mb-4" />
                  <h3 className="text-xl font-bold text-[#0A1628] mb-3">
                    {s.title}
                  </h3>
                  <p className="text-[#0A1628]/60 text-sm leading-relaxed">
                    {s.desc}
                  </p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </SectionReveal>

      {/* ── CTA ──────────────────────────────────────────── */}
      <section className="py-24 bg-[#0A1628]">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <h2 className="text-4xl md:text-5xl font-bold tracking-tight text-white mb-6">
            Find the Right Solution for Your Business
          </h2>
          <Link
            to="/contact"
            data-testid="services-cta"
            className="bg-[#00D68F] text-[#0A1628] px-10 py-4 font-bold hover:-translate-y-1 transition-all inline-flex items-center gap-2 text-lg"
          >
            Get a Free Assessment <ArrowRight size={20} />
          </Link>
        </div>
      </section>
    </div>
  );
}
